import {
  createCampaign,
  deleteCampaign,
  getAllCampaigns,
  getCampaign,
  updateCampaign,
  type Campaign,
} from "../helpers/campaign-api";
import { getAccessToken } from "../helpers/auth-helper";
import { getAllSignatures, type Signature } from "../helpers/signature-api";
import { SET_SIGNATURE_ASYNC_MAX_CHARS, SIGNATURE_SAVE_MAX_CHARS } from "../utils/signatureCharLimits";
import { runWithApiBusy, type ApiBusyLock } from "../utils/apiBusy";
import { showMessage } from "../helpers/message-helper";
import { isFreCarouselOpen } from "./fre-carousel-state";
import { fetchCapabilities, getAllowedAttachmentFormats, isImageAttachmentAllowed, isImageBase64Allowed, isImageUrlAllowed } from "../helpers/capabilities-helper";
import { initSignatureEditor } from "./signature-editor";
import {
  createSignaturePickerState,
  getSelectedSignatureIds,
  renderSignatureMappingBox,
  updateCampaignFormMappingLengthHint,
  getCampaignFormMappingLengthStatus,
  type SignaturePickerState,
} from "./signature-campaign-mapping-ui";
import {
  mountAudiencePicker,
  type AudiencePicker,
  type AudienceState,
} from "./audience-targeting-ui";

type TranslateFn = (key: string, params?: Record<string, string | number>) => string;

type CampaignAuthResume =
  | "campaigns"
  | "campaign-new"
  | { kind: "campaign-edit"; id: number };

type CampaignsPaneHooks = {
  /** Token abgelaufen / 401 — Session erneuern und Aktion fortsetzen. */
  onAuthLoss?: (reason: string, resume?: CampaignAuthResume) => void;
  /** Signaturen-Tab: Liste inkl. Reload (Session-Check). */
  onShowSignatures?: () => void;
};

let campaigns: Campaign[] = [];
let editingCampaignId: number | null = null;
let t: TranslateFn = (k) => k;
let onMappingsChanged: (() => void) | undefined;
let paneHooks: CampaignsPaneHooks = {};
let getProfileFields: () => { id: string; label: string; token: string; hasValue?: boolean; value?: string }[] = () =>
  [];
let signaturePickerState: SignaturePickerState | null = null;
let campaignEditorApi: {
  getContent: () => string;
  setContent: (html: string) => void;
  didBlockDisallowedContent?: () => boolean;
} | null = null;
let campaignFormSignatures: Signature[] = [];
let campaignFormMappingOk = true;
let campaignAudiencePicker: AudiencePicker | null = null;
const campaignSaveLock: ApiBusyLock = { inFlight: false };
const campaignDeleteLock: ApiBusyLock = { inFlight: false };

function destroyCampaignEditor(): void {
  campaignEditorApi = null;
  campaignAudiencePicker = null;
  const wrap = document.getElementById("campaign-editor-wrap");
  if (wrap) wrap.innerHTML = "";
}

async function initCampaignEditor(content: string): Promise<void> {
  destroyCampaignEditor();
  const caps = await fetchCapabilities();
  const refreshMappingLength = (): void => {
    const html = campaignEditorApi?.getContent() ?? "";
    campaignFormMappingOk = updateCampaignFormMappingLengthHint(
      t,
      html,
      campaignFormSignatures,
      selectedVisibleSignatureIds()
    );
    const submitBtn = document.querySelector(
      "#campaign-form button[type='submit']"
    ) as HTMLButtonElement | null;
    if (submitBtn) submitBtn.disabled = !campaignFormMappingOk;
  };
  campaignEditorApi = initSignatureEditor("campaign-editor-wrap", t, getProfileFields(), showMessage, {
    showProfileFields: true,
    placeholderKey: "campaigns.placeholderContent",
    allowImageUrl: isImageUrlAllowed(caps),
    allowImageBase64: isImageBase64Allowed(caps),
    allowImageAsAttachment: isImageAttachmentAllowed(caps),
    allowedAttachmentFormats: getAllowedAttachmentFormats(caps),
    onContentChange: refreshMappingLength,
  });
  campaignEditorApi.setContent(content);
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function setSectionVisible(id: string, visible: boolean): void {
  const el = document.getElementById(id);
  if (el) el.style.display = visible ? "" : "none";
}

function hideEditorBottomChrome(): void {
  const bottom = document.getElementById("sunnysign-bottom-chrome");
  if (bottom) bottom.style.display = "none";
}

function restoreEditorBottomChrome(): void {
  const bottom = document.getElementById("sunnysign-bottom-chrome");
  if (bottom) bottom.style.display = "";
}

function setCampaignFormLoading(loading: boolean): void {
  const loadingEl = document.getElementById("campaign-form-loading");
  const contentEl = document.getElementById("campaign-form-content");
  if (loadingEl) loadingEl.style.display = loading ? "flex" : "none";
  if (contentEl) contentEl.style.display = loading ? "none" : "";
}

function hideSignatureViews(): void {
  setSectionVisible("signature-list-section", false);
  setSectionVisible("signature-form-section", false);
  setSectionVisible("signature-preview-section", false);
  setSectionVisible("settings-section", false);
  setEditorMainTabsVisible(false);
  hideEditorBottomChrome();
}

export function setEditorMainTabsVisible(visible: boolean): void {
  const tabs = document.getElementById("editor-main-tabs");
  if (!tabs) return;
  if (!visible || isFreCarouselOpen()) {
    tabs.style.display = "none";
    return;
  }
  if (tabs.dataset.enabled !== "true") return;
  tabs.style.display = "";
}

export function setActiveMainTab(tab: "signatures" | "campaigns"): void {
  const sigTab = document.getElementById("main-tab-signatures");
  const campTab = document.getElementById("main-tab-campaigns");
  if (sigTab) {
    sigTab.classList.toggle("is-active", tab === "signatures");
    sigTab.setAttribute("aria-selected", tab === "signatures" ? "true" : "false");
  }
  if (campTab) {
    campTab.classList.toggle("is-active", tab === "campaigns");
    campTab.setAttribute("aria-selected", tab === "campaigns" ? "true" : "false");
  }
}

export function updateEditorMainTabsVisibility(canManage: boolean): void {
  const tabs = document.getElementById("editor-main-tabs");
  if (!tabs) return;
  tabs.dataset.enabled = canManage ? "true" : "false";
  document.documentElement.classList.toggle("sunnysign-editor-tabs-visible", canManage);
  if (!canManage) {
    setActiveMainTab("signatures");
    tabs.style.display = "none";
    return;
  }
  if (isFreCarouselOpen()) {
    tabs.style.display = "none";
    return;
  }
  tabs.style.display = "";
}

function showSignatureListView(): void {
  if (isFreCarouselOpen()) return;
  setSectionVisible("campaigns-list-section", false);
  setSectionVisible("campaign-form-section", false);
  setSectionVisible("signature-list-section", true);
  setActiveMainTab("signatures");
  setEditorMainTabsVisible(true);
  restoreEditorBottomChrome();
}

/**
 * Nur Signaturen mitschicken, die im Formular auch sichtbar sind. Eine
 * Auswahl, die der Nutzer nicht sehen und damit nicht abwählen kann (z. B. eine
 * inzwischen gelöschte Signatur), würde jedes Speichern mit 400 abbrechen.
 */
function selectedVisibleSignatureIds(): number[] {
  const selected = signaturePickerState ? getSelectedSignatureIds(signaturePickerState) : [];
  if (campaignFormSignatures.length === 0) return selected;
  const known = new Set(campaignFormSignatures.map((s) => s.id));
  return selected.filter((id) => known.has(id));
}

/** Fehlerschlüssel aus der API-Antwort; sonst der allgemeine Speicherfehler. */
function campaignErrorMessage(err: unknown): string {
  const response = (err as { responseJSON?: { error?: string }; responseText?: string } | null) ?? null;
  const code = response?.responseJSON?.error ?? response?.responseText ?? String(err);
  if (code.includes("campaign_mapping_signature_required")) return t("campaigns.mappingSignatureRequired");
  if (code.includes("campaign_name_required")) return t("campaigns.nameRequired");
  return `${t("campaigns.saveFailed")}: ${code}`;
}

function toIsoOrNull(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const date = new Date(trimmed);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function toLocalInputValue(iso?: string | null): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function formatCampaignPeriod(campaign: Campaign): string {
  const start = campaign.startsAtUtc ? new Date(campaign.startsAtUtc).toLocaleString() : "—";
  const end = campaign.endsAtUtc ? new Date(campaign.endsAtUtc).toLocaleString() : "—";
  return `${start} → ${end}`;
}

/** Ladehinweis in der Kampagnenliste, bis die API antwortet. */
function showCampaignListSpinner(): void {
  const container = document.getElementById("campaigns-container");
  if (!container) return;
  container.innerHTML = `<div class="sunnysign-loading" aria-live="polite" aria-busy="true"><span class="sunnysign-spinner" role="status"></span><span>${escapeHtml(t("campaigns.loading"))}</span></div>`;
}

function isAuthLossError(error: unknown): boolean {
  const msg = String((error as { message?: string } | null)?.message ?? error ?? "");
  if (/not_authenticated/i.test(msg)) return true;
  if (/nicht angemeldet/i.test(msg)) return true;
  if (/not signed in|please sign in/i.test(msg)) return true;
  const statusCode =
    typeof (error as { status?: unknown } | null)?.status === "number"
      ? (error as { status: number }).status
      : typeof (error as { statusCode?: unknown } | null)?.statusCode === "number"
        ? (error as { statusCode: number }).statusCode
        : undefined;
  return statusCode === 401;
}

async function loadCampaigns(): Promise<void> {
  showCampaignListSpinner();
  campaigns = await getAllCampaigns();
  renderCampaignList();
}

function renderCampaignList(): void {
  const container = document.getElementById("campaigns-container");
  if (!container) return;

  if (campaigns.length === 0) {
    container.innerHTML = `<p class="sunnysign-section__title" style="margin-top:0;font-weight:400;font-size:10px;">${escapeHtml(t("campaigns.empty"))}</p>`;
    return;
  }

  const iconMore =
    '<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/></svg>';
  const iconPencil =
    '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg>';
  const iconTrash =
    '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></svg>';

  container.innerHTML = campaigns
    .map(
      (campaign) => `
    <div class="signature-item campaign-item" data-campaign-id="${campaign.id}">
      <div class="signature-item-title-row">
        <h3 class="signature-item-title">${escapeHtml(campaign.name)}</h3>
        <div class="signature-item-menu-wrap">
          <button type="button" class="signature-item-menu-trigger campaign-menu-trigger" data-id="${campaign.id}" aria-label="${escapeHtml(t("list.menu"))}">${iconMore}</button>
          <div class="signature-item-context-menu" role="menu" aria-hidden="true">
            <button type="button" class="signature-item-menu-edit campaign-edit-btn" data-id="${campaign.id}" role="menuitem"><span class="signature-item-menu-icon">${iconPencil}</span>${escapeHtml(t("list.edit"))}</button>
            <button type="button" class="signature-item-menu-delete campaign-delete-btn" data-id="${campaign.id}" role="menuitem"><span class="signature-item-menu-icon">${iconTrash}</span>${escapeHtml(t("list.delete"))}</button>
          </div>
        </div>
      </div>
      <p class="campaign-item-period">${escapeHtml(formatCampaignPeriod(campaign))}</p>
    </div>`
    )
    .join("");

  container.querySelectorAll(".campaign-menu-trigger").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      const wrap = (btn as HTMLElement).closest(".signature-item-menu-wrap");
      const menu = wrap?.querySelector(".signature-item-context-menu") as HTMLElement | null;
      const wasOpen = menu?.getAttribute("aria-hidden") === "false";
      container.querySelectorAll(".signature-item-context-menu[aria-hidden=false]").forEach((m) => {
        m.setAttribute("aria-hidden", "true");
      });
      if (menu && !wasOpen) menu.setAttribute("aria-hidden", "false");
    });
  });

  container.querySelectorAll(".campaign-edit-btn").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      const id = parseInt((btn as HTMLElement).getAttribute("data-id") || "0", 10);
      void showCampaignForm(id);
    });
  });

  container.querySelectorAll(".campaign-delete-btn").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      const id = parseInt((btn as HTMLElement).getAttribute("data-id") || "0", 10);
      void onDeleteCampaign(id, btn as HTMLButtonElement);
    });
  });
}

async function renderCampaignSignatureMapping(signatureIds: number[]): Promise<void> {
  const container = document.getElementById("campaign-signature-mapping");
  if (!container) return;

  try {
    const orgSignatures = await getAllSignatures();
    campaignFormSignatures = orgSignatures;
    container.innerHTML = "";
    signaturePickerState = createSignaturePickerState(signatureIds);
    const refreshMappingLength = (): void => {
      const html = campaignEditorApi?.getContent() ?? "";
      campaignFormMappingOk = updateCampaignFormMappingLengthHint(
        t,
        html,
        campaignFormSignatures,
        selectedVisibleSignatureIds()
      );
      const submitBtn = document.querySelector(
        "#campaign-form button[type='submit']"
      ) as HTMLButtonElement | null;
      if (submitBtn) submitBtn.disabled = !campaignFormMappingOk;
    };
    renderSignatureMappingBox(container, orgSignatures, signaturePickerState, t, () => {
      refreshMappingLength();
    });
    refreshMappingLength();
  } catch (err) {
    container.innerHTML = `<p class="sunnysign-error">${escapeHtml(String(err))}</p>`;
  }
}

async function showCampaignForm(id?: number): Promise<void> {
  const resume: CampaignAuthResume =
    id != null ? { kind: "campaign-edit", id } : "campaign-new";
  if (!getAccessToken()) {
    paneHooks.onAuthLoss?.("Kampagnen-Formular: kein Token", resume);
    return;
  }

  editingCampaignId = id ?? null;
  setSectionVisible("campaigns-list-section", false);
  setSectionVisible("campaign-form-section", true);
  setEditorMainTabsVisible(false);
  setCampaignFormLoading(true);
  hideEditorBottomChrome();

  const title = document.getElementById("campaign-form-title");
  const nameInput = document.getElementById("campaign-name") as HTMLInputElement | null;
  const startInput = document.getElementById("campaign-starts-at") as HTMLInputElement | null;
  const endInput = document.getElementById("campaign-ends-at") as HTMLInputElement | null;

  if (title) title.textContent = id ? t("campaigns.editTitle") : t("campaigns.newTitle");
  if (nameInput) nameInput.value = "";
  let audienceState: AudienceState = { targetAll: true, groups: [] };
  destroyCampaignEditor();
  if (startInput) {
    startInput.value = "";
    startInput.removeAttribute("value");
  }
  if (endInput) {
    endInput.value = "";
    endInput.removeAttribute("value");
  }

  try {
    let signatureIds: number[] = [];
    let content = "";
    if (id) {
      const campaign = await getCampaign(id);
      if (nameInput) nameInput.value = campaign.name;
      content = campaign.content ?? "";
      if (startInput) startInput.value = toLocalInputValue(campaign.startsAtUtc);
      if (endInput) endInput.value = toLocalInputValue(campaign.endsAtUtc);
      signatureIds = campaign.signatureIds ?? [];
      audienceState = {
        targetAll: campaign.targetAll !== false,
        groups: campaign.targetGroups ?? [],
      };
    } else {
      const orgSignatures = await getAllSignatures();
      signatureIds = orgSignatures.map((s) => s.id);
    }

    await initCampaignEditor(content);
    campaignAudiencePicker = mountAudiencePicker("campaign-audience-targeting", t, audienceState);
    await renderCampaignSignatureMapping(signatureIds);
  } catch (err) {
    showMessage(`${t("campaigns.loadFailed")}: ${String(err)}`);
    if (isAuthLossError(err)) {
      paneHooks.onAuthLoss?.("Kampagnen-Formular: Session abgelaufen", resume);
      return;
    }
    showCampaignList();
  } finally {
    setCampaignFormLoading(false);
  }
}

function showCampaignList(): void {
  if (isFreCarouselOpen()) return;
  editingCampaignId = null;
  destroyCampaignEditor();
  setSectionVisible("campaign-form-section", false);
  setSectionVisible("campaigns-list-section", true);
  setActiveMainTab("campaigns");
  setEditorMainTabsVisible(true);
  restoreEditorBottomChrome();
}

async function onCampaignFormSubmit(e: Event): Promise<void> {
  e.preventDefault();
  if (campaignSaveLock.inFlight) return;

  const nameInput = document.getElementById("campaign-name") as HTMLInputElement | null;
  const startInput = document.getElementById("campaign-starts-at") as HTMLInputElement | null;
  const endInput = document.getElementById("campaign-ends-at") as HTMLInputElement | null;
  if (!nameInput || !campaignEditorApi) return;

  const payload = {
    name: nameInput.value.trim(),
    content: campaignEditorApi.getContent(),
    startsAtUtc: toIsoOrNull(startInput?.value ?? ""),
    endsAtUtc: toIsoOrNull(endInput?.value ?? ""),
    signatureIds: selectedVisibleSignatureIds(),
    targetAll: campaignAudiencePicker?.getState().targetAll ?? true,
    targetGroupIds: campaignAudiencePicker?.getState().groups.map((group) => group.id) ?? [],
  };
  const sanitizeBlocked = campaignEditorApi.didBlockDisallowedContent?.() === true;

  if (!payload.name) {
    showMessage(t("campaigns.nameRequired"));
    return;
  }

  if (payload.content.length > SIGNATURE_SAVE_MAX_CHARS) {
    showMessage(
      t("msg.tooLong", {
        len: String(payload.content.length),
        saveMax: String(SIGNATURE_SAVE_MAX_CHARS),
        max: String(SET_SIGNATURE_ASYNC_MAX_CHARS),
      })
    );
    return;
  }

  if (payload.signatureIds.length === 0) {
    showMessage(t("campaigns.mappingSignatureRequired"));
    return;
  }

  const mappingStatus = getCampaignFormMappingLengthStatus(
    payload.content,
    campaignFormSignatures,
    payload.signatureIds
  );
  if (mappingStatus.overSlot) {
    showMessage(
      t("campaigns.mappingTooLong", {
        len: String(mappingStatus.estimated),
        max: String(SET_SIGNATURE_ASYNC_MAX_CHARS),
      })
    );
    return;
  }

  const submitBtn = document.querySelector(
    "#campaign-form button[type='submit']"
  ) as HTMLButtonElement | null;

  await runWithApiBusy({ buttons: [submitBtn], lock: campaignSaveLock }, async () => {
    try {
      if (editingCampaignId) {
        await updateCampaign(editingCampaignId, payload);
        showMessage(
          sanitizeBlocked
            ? `${t("campaigns.saved")} ${t("editor.contentSanitized")}`
            : t("campaigns.saved")
        );
      } else {
        await createCampaign(payload);
        showMessage(
          sanitizeBlocked
            ? `${t("campaigns.created")} ${t("editor.contentSanitized")}`
            : t("campaigns.created")
        );
      }
      await loadCampaigns();
      onMappingsChanged?.();
      showCampaignList();
    } catch (err) {
      if (isAuthLossError(err)) {
        const resume: CampaignAuthResume =
          editingCampaignId != null
            ? { kind: "campaign-edit", id: editingCampaignId }
            : "campaign-new";
        paneHooks.onAuthLoss?.("Kampagnen speichern: Session abgelaufen", resume);
        return;
      }
      showMessage(campaignErrorMessage(err));
    }
  });
}

async function onDeleteCampaign(id: number, triggerBtn?: HTMLButtonElement): Promise<void> {
  if (campaignDeleteLock.inFlight) return;
  const ok = window.confirm(t("campaigns.deleteConfirm"));
  if (!ok) return;
  await runWithApiBusy({ buttons: [triggerBtn ?? null], lock: campaignDeleteLock }, async () => {
    try {
      await deleteCampaign(id);
      showMessage(t("campaigns.deleted"));
      await loadCampaigns();
      onMappingsChanged?.();
    } catch (err) {
      if (isAuthLossError(err)) {
        paneHooks.onAuthLoss?.("Kampagnen löschen: Session abgelaufen", "campaigns");
        return;
      }
      showMessage(`${t("campaigns.deleteFailed")}: ${String(err)}`);
    }
  });
}

export async function openCampaignsPane(): Promise<void> {
  if (isFreCarouselOpen()) return;
  if (!getAccessToken()) {
    paneHooks.onAuthLoss?.("Kampagnen: kein Token", "campaigns");
    return;
  }
  hideSignatureViews();
  setSectionVisible("campaign-form-section", false);
  setSectionVisible("campaigns-list-section", true);
  setActiveMainTab("campaigns");
  setEditorMainTabsVisible(true);
  restoreEditorBottomChrome();
  try {
    await loadCampaigns();
  } catch (err) {
    if (isAuthLossError(err)) {
      paneHooks.onAuthLoss?.("Kampagnen: kein Token oder 401", "campaigns");
      return;
    }
    showMessage(`${t("campaigns.loadFailed")}: ${String(err)}`);
    showSignatureListView();
  }
}

/** Öffentliche Einstiege für Session-Resume nach SSO. */
export async function openCampaignForm(id?: number): Promise<void> {
  hideSignatureViews();
  setSectionVisible("campaigns-list-section", false);
  setActiveMainTab("campaigns");
  await showCampaignForm(id);
}

export function initCampaignsPane(
  translate: TranslateFn,
  mappingsChanged?: () => void,
  profileFields?: () => { id: string; label: string; token: string; hasValue?: boolean; value?: string }[],
  hooks?: CampaignsPaneHooks
): void {
  t = translate;
  onMappingsChanged = mappingsChanged;
  getProfileFields = profileFields ?? (() => []);
  paneHooks = hooks ?? {};

  document.getElementById("main-tab-signatures")?.addEventListener("click", () => {
    if (paneHooks.onShowSignatures) {
      paneHooks.onShowSignatures();
      return;
    }
    showSignatureListView();
  });
  document.getElementById("main-tab-campaigns")?.addEventListener("click", () => {
    void openCampaignsPane();
  });
  document.getElementById("campaign-form-back-button")?.addEventListener("click", () => {
    showCampaignList();
  });
  document.getElementById("campaign-form")?.addEventListener("submit", (e) => {
    void onCampaignFormSubmit(e);
  });
  document.getElementById("add-campaign-button")?.addEventListener("click", () => {
    void showCampaignForm();
  });
}
